import { ISnippet } from '../types'
import { IAnswerItem, IQuestion } from '../../../types'
import { QuestionTypeEnum } from '../../../common/QuestionFieldType'

export default class QuestionType {
  readonly id: number
  readonly fieldType: string
  readonly isTest: boolean

  readonly label: string
  readonly sampleCode: string
  readonly focusPosition: Position

  constructor(fieldType: string, isTest: boolean) {
    this.fieldType = fieldType
    this.isTest = isTest

    switch (fieldType) {
      case QuestionTypeEnum.CHECKBOX:
        if (isTest) {
          this.label = 'チェックボックス式問題'
          this.sampleCode = '$${1:10} [ ] ${2:正回答}\n$   [ ] ${3:不正回答}\n'
        } else {
          this.label = 'チェックボックス式'
          this.sampleCode = '$ [ ] ${1:回答}\n$ [ ] ${2:回答}\n'
        }
        break
      case QuestionTypeEnum.MULTI_CHECKBOX:
        this.label = 'チェックボックス式問題（全問正解で得点）'
        this.sampleCode = '$* [ ] ${1:正回答}\n$* [ ] ${2:正回答}\n$  [ ] ${3:不正回答}\n$*${4:10}\n'
        break
      case QuestionTypeEnum.RADIO:
        if (isTest) {
          this.label = 'ラジオボタン式問題'
          this.sampleCode = '$${1:10} ( ) ${2:正回答}\n$   ( ) ${3:不正回答}\n'
        } else {
          this.label = 'ラジオボタン式'
          this.sampleCode = '$ ( ) ${1:回答}\n$ ( ) ${2:回答}\n'
        }
        break
      case QuestionTypeEnum.TEXTFIELD:
        if (isTest) {
          this.label = 'テキストフィルド式問題（自動採点）'
          this.sampleCode = '$(${1:正回答}, ${2:正回答} | ${3:10})____ '
        } else {
          this.label = 'テキストフィルド式'
          this.sampleCode = '\\$____ '
        }
        break
      case QuestionTypeEnum.TEXTAREA:
        if (isTest) {
          this.label = 'テキストエリア式問題（人間が採点）'
          this.sampleCode = '$([${1:good}, ${2:10}], [${3:normal}, ${4:5}], [${5:bad}, ${6:0}]) =====\n'
        } else {
          this.label = 'テキストエリア式'
          this.sampleCode = '$ =====\n'
        }
        break
      case QuestionTypeEnum.SELECT:
        if (isTest) {
          this.label = 'セレクト式問題'
          this.sampleCode = '$${1:10} [ ${2:正回答} ]\n$   [ ${3:不正回答} ]\n'
        } else {
          this.label = 'セレクト式'
          this.sampleCode = '$ [ ${1:回答} ]\n$ [ ${2:回答} ]\n'
        }
        break
      case QuestionTypeEnum.INLINE_SELECT:
        if (isTest) {
          this.label = 'インラインセレクト式問題'
          this.sampleCode = '$(*[${1:good}, ${2:10}], [${3:normal}, ${4:5}], [${5:bad}, ${6:0}])____ '
        } else {
          this.label = 'インラインセレクト式'
          this.sampleCode = '$(*[${1:good}, ${2:10}], [${3:normal}, ${4:5}], [${5:bad}, ${6:0}])____ '
        }
        break
    }
  }

  private multilineSnippets(template: string, linesCount: number, splitter = ''): string {
    const output = []
    let varCount = 0
    template.replace(/\${([0-9]+):[^}]*}/g, (match, number) => {
      number = parseInt(number)
      varCount = Math.max(varCount, number)
      return ''
    })
    for (let i = 0; i < linesCount; i++) {
      output.push(
        template.replace(
          /\${([0-9]+):([^}]*)}/g,
          (match, number, label) => `\${${i * varCount + parseInt(number)}:${label.replace('%i', i + 1)}}`
        )
      )
    }
    return output.join(splitter)
  }

  public get snippets(): ISnippet[] {
    const snippets: ISnippet[] = []
    const maxAnswer = 10
    switch (this.fieldType) {
      case QuestionTypeEnum.CHECKBOX:
        if (this.isTest) {
          snippets.push({
            content: '$${1:1} [ ] ${2:option}\n',
            name: this.label,
            tabTrigger: 'qc'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$${1:%i} [ ] ${2:option%i}\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `qc${i}`
            })
          }
        } else {
          snippets.push({
            content: '$ [ ] ${1:option}\n',
            name: this.label,
            tabTrigger: 'ec'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$ [ ] ${1:option%i}\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `ec${i}`
            })
          }
        }
        break
      case QuestionTypeEnum.MULTI_CHECKBOX:
        snippets.push({
          content: '$${1:*} [ ] ${2:option}\n',
          name: this.label,
          tabTrigger: 'qca'
        })
        for (let i = 1; i <= maxAnswer; i++) {
          snippets.push({
            content: this.multilineSnippets('$${1:*} [ ] ${2:option%i}\n', i) + '$*${' + (i * 2 + 1) + ':10}\n',
            name: `${this.label}${i}`,
            tabTrigger: `qca${i}`
          })
        }
        break
      case QuestionTypeEnum.RADIO:
        if (this.isTest) {
          snippets.push({
            content: '$${1:1} ( ) ${2:option}\n',
            name: this.label,
            tabTrigger: 'qr'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$${1:%i} ( ) ${2:option%i}\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `qr${i}`
            })
          }
        } else {
          snippets.push({
            content: '$ ( ) ${1:option}\n',
            name: this.label,
            tabTrigger: 'er'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$ ( ) ${1:option%i}\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `er${i}`
            })
          }
        }
        break
      case QuestionTypeEnum.TEXTFIELD:
        if (this.isTest) {
          snippets.push({
            content: '$(${1:answer} | ${2:1})___',
            name: this.label,
            tabTrigger: 'qt'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: `\$(${this.multilineSnippets('${1:answer%i}', i, ', ')} | \${${i + 1}:1})___`,
              name: `${this.label}${i}`,
              tabTrigger: `qt${i}`
            })
          }
        } else {
          snippets.push({
            content: '\\$___ ',
            name: this.label,
            tabTrigger: 'etf'
          })
        }
        break
      case QuestionTypeEnum.TEXTAREA:
        if (this.isTest) {
          snippets.push({
            content: '$([${1:correct}, ${2:10}], [${3:wrong}, ${4:0}])===\n',
            name: this.label,
            tabTrigger: 'qta'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: `$(${this.multilineSnippets('[${1:level%i}, ${2:%i}]', i, ', ')})===\n`,
              name: `${this.label}${i}`,
              tabTrigger: `qta${i}`
            })
          }
        } else {
          snippets.push({
            content: '$===\n',
            name: this.label,
            tabTrigger: 'eta'
          })
        }
        break
      case QuestionTypeEnum.SELECT:
        if (this.isTest) {
          snippets.push({
            content: '$${1:1} [ ${2:option} ]\n',
            name: this.label,
            tabTrigger: 'qs'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$${1:%i} [ ${2:option%i} ]\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `qs${i}`
            })
          }
        } else {
          snippets.push({
            content: '$ [ ${1:option} ]\n',
            name: this.label,
            tabTrigger: 'es'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: this.multilineSnippets('$${1:%i} [ ${2:option%i} ]\n', i),
              name: `${this.label}${i}`,
              tabTrigger: `es${i}`
            })
          }
        }
        break
      case QuestionTypeEnum.INLINE_SELECT:
        if (this.isTest) {
          snippets.push({
            content: '$(*[${1:correct}, ${2:10}], [${3:wrong}, ${4:0}])_______\n',
            name: this.label,
            tabTrigger: 'qis'
          })
          for (let i = 1; i <= maxAnswer; i++) {
            snippets.push({
              content: `$(*${this.multilineSnippets('[${1:level%i}, ${2:%i}]', i, ', ')})______\n`,
              name: `${this.label}${i}`,
              tabTrigger: `qis${i}`
            })
          }
        } else {
          snippets.push({
            content: '$(*[${1:correct}, ${2:10}], [${3:wrong}, ${4:0}])_______\n',
            name: this.label,
            tabTrigger: 'eis'
          })
        }
        break
    }

    // Polly Speak Snippset
    snippets.push({
      content: '<audio controls type="speak" lang="${1:en-US}" voice="${2:Kendra}">\n\t${3:Hello Edbase}\n</audio>\n',
      name: 'audio',
      tabTrigger: 'audio'
    })

    // Polly SSML support 
    // https://docs.aws.amazon.com/polly/latest/dg/supportedtags.html#speak-tag
    snippets.push({
      content: '<speak>\n\t${1:Hello Edbase}\n</speak>\n',
      name: 'speak',
      tabTrigger: 'speak'
    })
    snippets.push({
      content: '<emphasis level="${1:strong}">${2:Hello Edbase}</emphasis>',
      name: 'emphasis',
      tabTrigger: 'emphasis'
    })
    snippets.push({
      content: '<lang xml:lang="${1:en-US}">${2:Hello Edbase}</lang>',
      name: 'lang',
      tabTrigger: 'lang'
    })
    snippets.push({
      content: '<mark name="${1:Edbase}"/>',
      name: 'mark',
      tabTrigger: 'mark'
    })
    snippets.push({
      content: '<p>${1:Hello Edbase}</p>',
      name: 'p',
      tabTrigger: 'p'
    })
    snippets.push({
      content: '<phoneme alphabet="${1:ipa}" ph="${2:pɪˈkɑːn}">${3:pecan}</phoneme>',
      name: 'phoneme',
      tabTrigger: 'phoneme'
    })
    snippets.push({
      content: '<prosody volume="${1:loud}" rate="${2:slow}" pitch="${3:high}" amazon:max-duration="${4:2s}">${5:Hello Edbase}</prosody>',
      name: 'prosody',
      tabTrigger: 'prosody'
    })
    snippets.push({
      content: '<s>${1:Hello Edbase}</s> ',
      name: 's',
      tabTrigger: 's'
    })
    snippets.push({
      content: '<say-as interpret-as="${1:value}">${2:Hello Edbase}</say-as>',
      name: 'say-as',
      tabTrigger: 'say-as'
    })
    snippets.push({
      content: '<sub alias="${1:new word}">${2:Hello Edbase}</sub>',
      name: 'sub',
      tabTrigger: 'sub'
    })
    snippets.push({
      content: '<w role="${1:amazon:VB}">${2:Hello Edbase}</w>',
      name: 'w',
      tabTrigger: 'w'
    })
    snippets.push({
      content: '<amazon:breath duration="${1:medium}" volume="${2:x-loud}"/>',
      name: 'breath',
      tabTrigger: 'breath'
    })
    snippets.push({
      content: '<amazon:domain name="${1:conversational}">${2:Hello Edbase}</amazon:domain>',
      name: 'domain',
      tabTrigger: 'domain'
    })
    snippets.push({
      content: '<amazon:effect name="${1:conversational}">${2:Hello Edbase}</amazon:effect>',
      name: 'effect',
      tabTrigger: 'effect'
    })
    
    return snippets
  }

  public nextSuggest(question: IQuestion<IAnswerItem>) {
    let points = 10
    switch (this.fieldType) {
      case QuestionTypeEnum.CHECKBOX:
        if (this.isTest) {
          return [
            { name: '$10 [ ] 正回答', snippet: `$\${1:${points}} [ ] \${2:正回答}\n`, meta: this.label },
            { name: '$ [ ] 不正回答', snippet: `$ [ ] \${1:不正回答}\n`, meta: this.label }
          ]
        } else {
          return [{ name: '$ [ ] 回答', snippet: `$ [ ] \${1:回答}\n`, meta: this.label }]
        }
      case QuestionTypeEnum.MULTI_CHECKBOX:
        let hasScore = false
        if (question.answerItems.find((ai) => ai.allotment != null)) {
          return [
            { name: '$* [ ] 正回答', snippet: `$* [ ] \${1:正回答}\n`, meta: 'チェックボックス式問題' },
            { name: '$ [ ] 不正回答', snippet: `$ [ ] \${1:不正回答}\n`, meta: 'チェックボックス式問題' }
          ]
        }
        return [
          { name: '$* [ ] 正回答', snippet: '$* [ ] ${1:正回答}\n', meta: 'チェックボックス式問題' },
          { name: '$ [ ] 不正回答', snippet: '$ [ ] ${1:不正回答}\n', meta: 'チェックボックス式問題' },
          { name: `$*${points}`, snippet: `$*\${1:${points}}\n`, meta: 'チェックボックス式問題' }
        ]
      case QuestionTypeEnum.RADIO:
        if (this.isTest) {
          let hasCorrectAnswer = question.answerItems.findIndex((ai) => ai.correct) >= 0
          let possibleInput: { name: string; snippet: string; meta: string }[] = []
          if (!hasCorrectAnswer) {
            possibleInput.push({
              name: `$${points} ( ) 正回答`,
              snippet: `$\${1:${points}} ( ) \${1:正回答}\n`,
              meta: this.label
            })
          }
          possibleInput.push({ name: '$ ( ) 不正回答', snippet: '$ ( ) ${1:不正回答}\n', meta: this.label })
          return possibleInput
        } else {
          return [{ name: '$ ( ) 回答', snippet: '$ ( ) ${1:回答}\n', meta: this.label }]
        }
      case QuestionTypeEnum.TEXTFIELD:
        if (this.isTest) {
          return [
            {
              name: `$(回答|${points})___`,
              snippet: `$(\${1:回答}|\${2:${points}})___ `,
              meta: 'テキストフィルド式問題'
            }
          ]
        } else {
          return [{ name: '$___', value: '$___ ', meta: 'テキストフィルド式問題' }]
        }
      case QuestionTypeEnum.TEXTAREA:
        if (question.fieldType) return []
        if (this.isTest) {
          return [
            { name: '$([回答, 10])===', snippet: `$([\${1:回答}, \${2:${points}}])===\n`, meta: 'テキストエリア式問題' }
          ]
        } else {
          return [{ name: '$===', snippet: `$===\n`, meta: 'テキストエリア式問題' }]
        }
      case QuestionTypeEnum.SELECT:
        if (this.isTest) {
          return [
            { name: `$${points} [ 正回答 ]`, snippet: `$\${1:${points}} [ \${2:正回答} ]\n`, meta: this.label },
            { name: '$ [ 不正回答 ]', snippet: `$ [ \${1:不正回答} ]\n`, meta: this.label }
          ]
        } else {
          return [{ name: '$ [ 回答 ]', snippet: `$ [ \${1:回答} ]\n`, meta: this.label }]
        }
      case QuestionTypeEnum.INLINE_SELECT:
        if (this.isTest) {
          return [
            { name: `$${points} [ 正回答 ]`, snippet: `$\${1:${points}} [ \${2:正回答} ]\n`, meta: this.label },
            { name: '$ [ 不正回答 ]', snippet: `$ [ \${1:不正回答} ]\n`, meta: this.label }
          ]
        } else {
          return [{ name: '$ [ 回答 ]', snippet: `$ [ \${1:回答} ]\n`, meta: this.label }]
        }
    }
  }
}

export interface Position {
  row: number
  column: number
}
